import { useLocalObservable } from 'mobx-react-lite';
import { ElementType, useEffect } from 'react';

import { IcText16Icon, IcUp16Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { VoiceTranscriptionStatus } from 'APP/model/message/messageModel.types';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

interface IVoicePayloadPresenter {
  message: ChatMessage;
  isCollapsed: boolean;
  isDisabled: boolean;
  icon: ElementType | null;
  isProgress: boolean;
  hasError: boolean;
  content: string | null;
  onClick(): Promise<void>;
  expand(): void;
  setMessage(message: ChatMessage): void;
}

export const useVoicePayloadPresenter = (message: ChatMessage): IVoicePayloadPresenter => {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IVoicePayloadPresenter>(() => ({
    message,
    isCollapsed: true,

    get isDisabled(): boolean {
      return presenter.message.isFake;
    },

    async onClick(): Promise<void> {
      if (presenter.isProgress || presenter.message.isFake) {
        return;
      }
      if (presenter.isCollapsed) {
        presenter.isCollapsed = false;
        await Tasks.messaging.transcribeVoiceMessage(presenter.message);
        return;
      }
      presenter.isCollapsed = true;
    },

    get icon(): ElementType | null {
      if (presenter.isProgress) {
        return null;
      }

      if (!presenter.isCollapsed) {
        return IcUp16Icon;
      }
      return IcText16Icon;
    },

    expand(): void {
      presenter.isCollapsed = false;
    },

    get isProgress(): boolean {
      return (
        presenter.message.payload.transcriptionStatus === VoiceTranscriptionStatus.InProgress &&
        !presenter.isCollapsed
      );
    },

    get hasError(): boolean {
      return (
        presenter.message.payload.transcriptionStatus ===
          VoiceTranscriptionStatus.TranscriptionFailed ||
        presenter.message.payload.transcriptionText === ''
      );
    },

    get content(): string | null {
      if (presenter.message.payload.transcriptionText && !presenter.isCollapsed) {
        return presenter.message.renderPayload.transcriptionText;
      }

      if (presenter.hasError && !presenter.isCollapsed) {
        return t('common_unable_recognize_text');
      }

      return null;
    },

    setMessage(message: ChatMessage): void {
      presenter.message = message;
    },
  }));

  useEffect(() => {
    presenter.setMessage(message);
  }, [message]);

  return presenter;
};
